import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react';
import {
  api,
  type AuthoredCourse,
  type CreatedStudent,
  type Student,
  type StudentCourse,
} from '../../api.ts';
import { Progress } from '../../components/Progress.tsx';
import { StudentEvents } from './StudentEvents.tsx';
import { StudentProgress } from './StudentProgress.tsx';
import { presence } from '../../lib/people.ts';

/**
 * The teacher's class: who is being taught, and what they are studying.
 *
 * Adding a pupil creates the account outright and posts them the login. There is no catalogue for
 * learners to browse and no self-registration — a course reaches a child because somebody here put
 * them into it, which makes this screen the actual gate on access.
 *
 * The password is shown once, in a panel the teacher dismisses. It exists nowhere readable
 * afterwards, so "I closed it by accident" means issuing a new one rather than looking it up.
 */
/** Whether this pupil is here, was here, or has never come at all. */
function Presence({ student }: { student: Student }): JSX.Element {
  const here = presence(student.last_seen_at);

  return (
    <div className={`presence presence--${here.state}`}>
      <span className="dot dot--small" aria-hidden="true" />
      <span>{here.label}</span>
      {student.status === 'invited' ? <span className="tag">приглашение отправлено</span> : null}
    </div>
  );
}

export function StudentsPage(): JSX.Element {
  const [students, setStudents] = useState<Student[] | null>(null);
  const [courses, setCourses] = useState<AuthoredCourse[]>([]);
  /*
   * Which enrolment is waiting for a second click.
   *
   * Asked in the page rather than through window.confirm. A browser told to stop showing dialogs
   * answers confirm() with false and says nothing at all, so the button does nothing and no
   * request is ever sent — which is exactly how this looked from the outside.
   */
  const [confirming, setConfirming] = useState('');
  /** Which pupil's history is open. One at a time: the panel is long, and comparing two of them
   * side by side is a different screen from this one. */
  /** Which pupil's card is open. One at a time: the card is long. */
  const [opened, setOpened] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<CreatedStudent | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const load = useCallback((): void => {
    void (async () => {
      try {
        setStudents(await api.authoring.students());
        setCourses(await api.authoring.courses());
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить список');
      }
    })();
  }, []);

  useEffect(load, [load]);

  async function addStudent(event: FormEvent): Promise<void> {
    event.preventDefault();

    setBusy('new');
    setError('');
    setNote('');

    try {
      const student = await api.authoring.createStudent({
        email: email.trim(),
        full_name: fullName.trim(),
        // Empty means "choose one for me", which is what adding a whole class calls for.
        ...(password.trim() ? { password: password.trim() } : {}),
      });

      setCreated(student);
      setFullName('');
      setEmail('');
      setPassword('');
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось добавить ученика');
    } finally {
      setBusy('');
    }
  }

  /**
   * Removes a pupil's access to the school.
   *
   * Confirmed first, and the confirmation says what survives: their submitted work and any
   * certificate they earned stay, because a certificate carries a number an employer can check
   * and deleting the record behind it would turn a document somebody is holding into a forgery.
   */
  async function remove(student: Student): Promise<void> {
    const sure = window.confirm(
      `Удалить ученика ${student.full_name}?

` +
        'Он потеряет доступ к школе и все зачисления на курсы. ' +
        'Сданные работы и выданные сертификаты сохранятся — по ним ещё можно будет ' +
        'проверить подлинность документа.',
    );

    if (!sure) return;

    setBusy(student.profile_id);
    setError('');
    setNote('');

    try {
      await api.authoring.removeStudent(student.profile_id);
      setNote(`${student.full_name} удалён из школы.`);
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось удалить ученика');
    } finally {
      setBusy('');
    }
  }


  /**
   * Taking a pupil off one course — the mistake this exists for is enrolling the wrong person.
   *
   * What happens depends on whether they have done anything, and only the server can see that, so
   * the confirmation promises nothing it cannot keep: it says the work will be kept, which is true
   * either way, and the note afterwards reports which of the two actually happened.
   */
  async function unenrol(student: Student, course: StudentCourse): Promise<void> {
    setConfirming('');
    setBusy(student.profile_id);
    setError('');
    setNote('');

    try {
      const { ending } = await api.authoring.endEnrollment(course.enrollment_id);

      setNote(
        ending === 'removed'
          ? `${student.full_name} отчислен с курса «${course.title}» — записи не осталось.`
          : `${student.full_name} отчислен с курса «${course.title}». Доступ закрыт, сданные работы сохранены.`,
      );
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось отчислить');
    } finally {
      setBusy('');
    }
  }
  async function enroll(student: Student): Promise<void> {
    const courseId = chosen[student.profile_id] ?? courses[0]?.id;
    if (!courseId) return;

    setBusy(student.profile_id);
    setError('');
    setNote('');

    try {
      await api.authoring.enroll(student.profile_id, courseId);
      const course = courses.find((c) => c.id === courseId);
      setNote(
        `${student.full_name} зачислен на курс «${course?.title ?? ''}»${
          course?.isPublished ? '' : ' — курс пока не опубликован, ученик его ещё не увидит'
        }.`,
      );
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось зачислить');
    } finally {
      setBusy('');
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Ученики</h1>
        <p className="muted">
          Вы создаёте ученику доступ и выдаёте логин с паролем. Курс появляется у него только после
          зачисления — списка курсов для самостоятельной записи нет.
        </p>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {note ? <div className="notice notice--done">{note}</div> : null}

      {created ? (
        <section className="editcard stack">
          <strong>Доступ создан — передайте его ученику</strong>

          <dl className="certcard__facts">
            <div>
              <dt>Ученик</dt>
              <dd>{created.full_name}</dd>
            </div>
            <div>
              <dt>Логин</dt>
              <dd>{created.email}</dd>
            </div>
            <div>
              <dt>Пароль</dt>
              <dd className="certcard__number certcard__number--inline">{created.password}</dd>
            </div>
          </dl>

          {created.email_delivered ? (
            <p className="muted">
              Письмо со ссылкой, логином и паролем отправлено на почту ученика.
            </p>
          ) : (
            <div className="notice notice--accent">
              Письмо отправить не удалось — почта пока не настроена на сервере. Передайте логин и
              пароль ученику лично: больше нигде их посмотреть нельзя.
            </div>
          )}

          <div className="row">
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => {
                void navigator.clipboard
                  .writeText(`Логин: ${created.email}\nПароль: ${created.password}`)
                  .then(() => setNote('Логин и пароль скопированы.'))
                  .catch(() => undefined);
              }}
            >
              Скопировать
            </button>
            <button type="button" className="btn btn--small" onClick={() => setCreated(null)}>
              Я записал, скрыть
            </button>
          </div>
        </section>
      ) : null}

      <form className="editcard stack" onSubmit={(e) => void addStudent(e)}>
        <strong>Новый ученик</strong>

        <div className="row">
          <div className="field" style={{ marginBottom: 0, flex: '1 1 220px' }}>
            <label htmlFor="student-name">Имя и фамилия</label>
            <input
              id="student-name"
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Пётр Ученик"
              required
            />
          </div>

          <div className="field" style={{ marginBottom: 0, flex: '1 1 220px' }}>
            <label htmlFor="student-email">Почта</label>
            <input
              id="student-email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="uchenik@example.com"
              required
            />
          </div>

          <div className="field" style={{ marginBottom: 0, flex: '1 1 200px' }}>
            <label htmlFor="student-password">Пароль</label>
            <input
              id="student-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="придумается сам"
              minLength={10}
            />
          </div>
        </div>

        <div className="row">
          <button className="btn" type="submit" disabled={busy === 'new'}>
            {busy === 'new' ? 'Создаём…' : 'Создать доступ и отправить письмо'}
          </button>
          <span className="muted" style={{ fontSize: '0.84rem' }}>
            Оставьте пароль пустым — придумаем читаемый, вроде <code>kavu-3mth-2r</code>.
          </span>
        </div>
      </form>

      {students === null && !error ? <p className="muted">Загружаем…</p> : null}

      {students?.length === 0 ? (
        <div className="notice">Пока никого. Добавьте первого ученика формой выше.</div>
      ) : null}

      {(students ?? []).map((student) => (
        <div key={student.profile_id} className="pupil">
          <div className="editrow">
            <span className="editrow__main">
              <strong>{student.full_name}</strong>
              <span className="muted"> · {student.email}</span>

              {/*
               * The status sits in the row rather than behind a click, because it is what the
               * list is scanned for: one glance down the column shows who is working and who was
               * handed an account and never came.
               */}
              <Presence student={student} />
              {/*
               * A bar per course rather than one grey run-on line. The teacher's question here is
               * "who is falling behind", and that is answered by glancing down a column of bars —
               * not by reading "Python с нуля — 0% · Scratch — 40%" five times over.
               */}
              {student.courses.length > 0 ? (
                <div className="enrolled">
                  {student.courses.map((c) => (
                    <div key={c.enrollment_id} className="enrolled__row">
                      <span className="enrolled__title" title={c.title}>
                        {c.title}
                      </span>
                      <Progress percent={c.progress_percent} compact />
                      {confirming === c.enrollment_id ? (
                        <span className="enrolled__ask">
                          отчислить?
                          <button
                            type="button"
                            className="linklike enrolled__yes"
                            disabled={busy === student.profile_id}
                            onClick={() => void unenrol(student, c)}
                          >
                            да
                          </button>
                          <button
                            type="button"
                            className="linklike enrolled__off"
                            onClick={() => setConfirming('')}
                          >
                            нет
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="linklike enrolled__off"
                          disabled={busy === student.profile_id}
                          onClick={() => setConfirming(c.enrollment_id)}
                          title="Отчислить с курса. Доступ закроется, сданные работы сохранятся"
                        >
                          отчислить
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="muted" style={{ fontSize: '0.84rem', marginTop: 2 }}>
                  пока не зачислен ни на один курс
                </div>
              )}
            </span>

            {courses.length > 0 ? (
              <>
                <select
                  className="input input--inline"
                  value={chosen[student.profile_id] ?? courses[0]?.id ?? ''}
                  onChange={(e) => setChosen({ ...chosen, [student.profile_id]: e.target.value })}
                  aria-label={`Курс для ${student.full_name}`}
                >
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                      {course.isPublished ? '' : ' (черновик)'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  disabled={busy === student.profile_id}
                  onClick={() => void enroll(student)}
                >
                  {busy === student.profile_id ? '…' : 'Зачислить'}
                </button>
              </>
            ) : null}

            {/*
             * The history opens under the pupil rather than on its own page: the question it answers
             * — "why is this one at 40%" — is asked while looking at the roll, and a page away is a
             * page too far to keep the rest of the class in mind.
             */}
            {/*
             * One card, not two. "How far has this child got", "what have they been doing" and
             * "are they here at all" are the same question asked three ways, and splitting the
             * answers across buttons made a teacher click twice to learn one thing.
             */}
            <button
              type="button"
              className="btn btn--ghost btn--small"
              aria-expanded={opened === student.profile_id}
              onClick={() =>
                setOpened((current) => (current === student.profile_id ? null : student.profile_id))
              }
            >
              {opened === student.profile_id ? 'Свернуть' : 'Подробнее'}
            </button>

            <button
              type="button"
              className="btn btn--ghost btn--small btn--danger"
              disabled={busy === student.profile_id}
              onClick={() => void remove(student)}
            >
              Удалить
            </button>
          </div>

          {opened === student.profile_id ? (
            <div className="pupil__history">
              <StudentProgress profileId={student.profile_id} />
              <StudentEvents profileId={student.profile_id} />
            </div>
          ) : null}
        </div>
      ))}

      {courses.length === 0 && students !== null ? (
        <div className="notice">
          Создайте курс на вкладке «Курсы» — тогда появится, куда зачислять.
        </div>
      ) : null}
    </>
  );
}
